import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { DRILL_TAGS, DRILL_CATEGORIES, DRILL_DIFFICULTIES } from '@/lib/drillTags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Plus, Upload, Edit, Trash2, Clock, BookOpen, X, Video
} from 'lucide-react';

const ShooterIcon = ({ className }) => (
  <svg viewBox="0 0 64 64" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="22" cy="8" r="5" />
    <path d="M18 16h8l3 6 8-4 2 4-11 6-3 8-4 18h-4l3-18-4-6-5 6-5 18h-4l7-24z" />
    <circle cx="48" cy="12" r="6" fill="none" stroke="currentColor" strokeWidth="2.5" />
    <line x1="42" y1="12" x2="54" y2="12" stroke="currentColor" strokeWidth="1.5" />
    <line x1="48" y1="6" x2="48" y2="18" stroke="currentColor" strokeWidth="1.5" />
    <path d="M44.5 7c2.5 2.5 2.5 7.5 0 10" fill="none" stroke="currentColor" strokeWidth="1" />
  </svg>
);

function toEmbedUrl(url) {
  if (!url) return null;
  let videoId = null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) videoId = u.pathname.slice(1);
    else if (u.hostname.includes('youtube.com')) videoId = u.searchParams.get('v');
  } catch { return url; }
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
}

export default function AdminDrillLibrary() {
  const [drills, setDrills] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [visFilter, setVisFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingDrill, setEditingDrill] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [videoDrill, setVideoDrill] = useState(null);

  const fetchDrills = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (tagFilter) params.append('tag', tagFilter);
      if (catFilter) params.append('category', catFilter);
      if (visFilter !== 'all') params.append('visibility', visFilter);
      params.append('limit', '200');
      const res = await api.get(`/drills?${params}`);
      setDrills(res.data.drills);
      setTotal(res.data.total);
      setSelected(new Set());
    } catch { toast.error('Failed to load drills'); }
    finally { setLoading(false); }
  }, [search, tagFilter, catFilter, visFilter]);

  useEffect(() => { fetchDrills(); }, [fetchDrills]);

  const handleDelete = async (drillId) => {
    try {
      await api.delete(`/drills/${drillId}`);
      toast.success('Drill deleted');
      setDeleteConfirm(null);
      fetchDrills();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/drills/bulk-delete', { drill_ids: [...selected] });
      toast.success(res.data.message);
      setBulkDeleteConfirm(false);
      setSelected(new Set());
      fetchDrills();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk delete failed'); }
    finally { setBulkDeleting(false); }
  };

  const toggleSelect = (drillId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(drillId)) next.delete(drillId);
      else next.add(drillId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === drills.length) setSelected(new Set());
    else setSelected(new Set(drills.map(d => d.drill_id)));
  };

  const catLabel = (val) => DRILL_CATEGORIES.find(c => c.value === val)?.label || val;

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight" data-testid="admin-drill-library-title">
            Drill Library
          </h1>
          <p className="text-slate-600 mt-1">{total} drill{total !== 1 ? 's' : ''} in library</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.size > 0 && (
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setBulkDeleteConfirm(true)} data-testid="bulk-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete {selected.size} Selected
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowImport(true)} data-testid="admin-import-drills-btn">
            <Upload className="w-4 h-4 mr-1" /> Import CSV
          </Button>
          <Button size="sm" className="bg-slate-900" onClick={() => { setEditingDrill(null); setShowForm(true); }} data-testid="admin-create-drill-btn">
            <Plus className="w-4 h-4 mr-1" /> New Drill
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[180px]">
            <Input placeholder="Search drills..." value={search} onChange={e => setSearch(e.target.value)}
              className="h-10" data-testid="admin-drill-search" />
          </div>
          <Select value={tagFilter || "_all"} onValueChange={v => setTagFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-[220px] h-10"><SelectValue placeholder="All Tags" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Tags</SelectItem>
              {DRILL_TAGS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={catFilter || "_all"} onValueChange={v => setCatFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-[160px] h-10"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Categories</SelectItem>
              {DRILL_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={visFilter} onValueChange={setVisFilter}>
            <SelectTrigger className="w-[140px] h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="public">Public</SelectItem>
              <SelectItem value="private">Private</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Drills Table */}
      <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
        {loading ? (
          <div className="px-4 py-12 text-center text-slate-500">Loading drills...</div>
        ) : drills.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <ShooterIcon className="w-14 h-14 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No drills found</p>
            <p className="text-sm text-slate-400 mt-1">Create your first drill or import from CSV</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  <th className="px-3 py-3 text-center w-10">
                    <input type="checkbox" checked={selected.size === drills.length && drills.length > 0}
                      onChange={toggleSelectAll} className="w-4 h-4 rounded border-slate-300 accent-slate-900 cursor-pointer"
                      data-testid="select-all-drills" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Category</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase text-slate-600">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Tags</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase text-slate-600">Visibility</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {drills.map(drill => (
                  <tr key={drill.drill_id} className={`hover:bg-slate-50 ${selected.has(drill.drill_id) ? 'bg-red-50/40' : ''}`}
                    data-testid={`admin-drill-row-${drill.drill_id}`}>
                    <td className="px-3 py-3 text-center">
                      <input type="checkbox" checked={selected.has(drill.drill_id)}
                        onChange={() => toggleSelect(drill.drill_id)}
                        className="w-4 h-4 rounded border-slate-300 accent-slate-900 cursor-pointer"
                        data-testid={`select-drill-${drill.drill_id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{drill.name}</p>
                          {drill.description && <p className="text-xs text-slate-400 truncate max-w-[250px]">{drill.description}</p>}
                        </div>
                        {drill.video_url && (
                          <button onClick={() => setVideoDrill(drill)} className="flex-shrink-0" data-testid={`play-drill-${drill.drill_id}`}>
                            <Video className="w-4 h-4 text-red-500 hover:text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{catLabel(drill.category)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-bold text-slate-700">{drill.duration_minutes}m</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[300px]">
                        {(drill.tags || []).map((t, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {drill.is_public ? (
                        <span className="text-[11px] px-2 py-0.5 bg-green-50 text-green-700 rounded-full font-bold">Public</span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-bold">Private</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`admin-edit-drill-${drill.drill_id}`}
                          onClick={() => { setEditingDrill(drill); setShowForm(true); }}>
                          <Edit className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`admin-delete-drill-${drill.drill_id}`}
                          onClick={() => setDeleteConfirm(drill)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <DrillFormModal open={showForm} drill={editingDrill}
        onClose={() => { setShowForm(false); setEditingDrill(null); }}
        onSaved={() => { setShowForm(false); setEditingDrill(null); fetchDrills(); }} />

      {/* Import Modal */}
      <DrillImportModal open={showImport} onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); fetchDrills(); }} />

      {/* Single Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-red-600 font-bold uppercase">Delete Drill</DialogTitle></DialogHeader>
          {deleteConfirm && (
            <div className="py-3">
              <p className="text-sm text-slate-600 mb-4">Permanently delete <strong>{deleteConfirm.name}</strong>?</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteConfirm.drill_id)} data-testid="confirm-delete-drill">Delete</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirm */}
      <Dialog open={bulkDeleteConfirm} onOpenChange={() => setBulkDeleteConfirm(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-red-600 font-bold uppercase">Bulk Delete Drills</DialogTitle></DialogHeader>
          <div className="py-3">
            <p className="text-sm text-slate-600 mb-2">
              Permanently delete <strong className="text-red-600">{selected.size}</strong> selected drill{selected.size !== 1 ? 's' : ''}?
            </p>
            <p className="text-xs text-slate-400 mb-4">This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setBulkDeleteConfirm(false)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="confirm-bulk-delete">
                {bulkDeleting ? 'Deleting...' : `Delete ${selected.size} Drill${selected.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Video Preview */}
      <Dialog open={!!videoDrill} onOpenChange={() => setVideoDrill(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-bold uppercase">{videoDrill?.name}</DialogTitle></DialogHeader>
          {videoDrill?.video_url && (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <iframe
                src={toEmbedUrl(videoDrill.video_url)}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={videoDrill.name}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Drill Create / Edit Form ─── */
function DrillFormModal({ open, drill, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', duration_minutes: 10, tags: [], category: 'skill_work', difficulty: 'intermediate', equipment: [], is_public: false, video_url: '' });
  const [saving, setSaving] = useState(false);
  const [equipInput, setEquipInput] = useState('');

  useEffect(() => {
    if (drill) {
      setForm({
        name: drill.name || '', description: drill.description || '',
        duration_minutes: drill.duration_minutes || 10, tags: drill.tags || [],
        category: drill.category || 'skill_work', difficulty: drill.difficulty || 'intermediate',
        equipment: drill.equipment || [], is_public: drill.is_public || false,
        video_url: drill.video_url || '',
      });
    } else {
      setForm({ name: '', description: '', duration_minutes: 10, tags: [], category: 'skill_work', difficulty: 'intermediate', equipment: [], is_public: false, video_url: '' });
    }
    setEquipInput('');
  }, [drill, open]);

  const toggleTag = (tag) => {
    setForm(f => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag] }));
  };

  const addEquip = () => {
    const val = equipInput.trim();
    if (val && !form.equipment.includes(val)) {
      setForm(f => ({ ...f, equipment: [...f.equipment, val] }));
      setEquipInput('');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (drill) {
        await api.put(`/drills/${drill.drill_id}`, form);
        toast.success('Drill updated');
      } else {
        await api.post('/drills', form);
        toast.success('Drill created');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-bold uppercase">{drill ? 'Edit Drill' : 'New Drill'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Name *</label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g., 3-Man Weave" data-testid="admin-drill-name-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Description</label>
            <textarea className="w-full border border-slate-200 rounded-lg p-2 text-sm min-h-[60px] resize-y focus:ring-1 focus:ring-slate-400 focus:outline-none"
              value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Brief drill description..." />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Video URL</label>
            <Input value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
              placeholder="https://www.youtube.com/watch?v=..." data-testid="admin-drill-video-input" />
            {form.video_url && toEmbedUrl(form.video_url) && (
              <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-black max-h-[180px]">
                <iframe src={toEmbedUrl(form.video_url)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Preview" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Duration (min)</label>
              <Input type="number" min={1} value={form.duration_minutes}
                onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 1 }))}
                data-testid="admin-drill-duration-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Category</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DRILL_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Difficulty</label>
              <Select value={form.difficulty} onValueChange={v => setForm(f => ({ ...f, difficulty: v }))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DRILL_DIFFICULTIES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Tags */}
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1.5 block">Tags (match PPA focus areas)</label>
            <div className="flex flex-wrap gap-1.5">
              {DRILL_TAGS.map(tag => (
                <button key={tag} type="button" onClick={() => toggleTag(tag)}
                  className={`text-[11px] px-2 py-1 rounded-full font-medium transition-colors ${form.tags.includes(tag) ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {tag}
                </button>
              ))}
            </div>
          </div>
          {/* Equipment */}
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Equipment</label>
            <div className="flex gap-2">
              <Input value={equipInput} onChange={e => setEquipInput(e.target.value)}
                placeholder="e.g., Cones" className="h-8 text-sm" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEquip())} />
              <Button size="sm" variant="outline" className="h-8" onClick={addEquip}>Add</Button>
            </div>
            {form.equipment.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {form.equipment.map((e, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-100 rounded-full flex items-center gap-1">
                    {e} <X className="w-3 h-3 cursor-pointer text-slate-400 hover:text-red-500" onClick={() => setForm(f => ({ ...f, equipment: f.equipment.filter((_, j) => j !== i) }))} />
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Visibility Toggle — iOS style green/gray */}
          <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
            <button type="button" onClick={() => setForm(f => ({ ...f, is_public: !f.is_public }))}
              className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${form.is_public ? 'bg-green-500' : 'bg-gray-300'}`}
              data-testid="admin-drill-visibility-toggle">
              <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-200 ${form.is_public ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </button>
            <div>
              <p className="text-sm font-semibold text-slate-700">{form.is_public ? 'Public' : 'Private'}</p>
              <p className="text-xs text-slate-500">{form.is_public ? 'Visible to all coaches' : 'Hidden from coaches'}</p>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1 bg-slate-900" onClick={handleSave} disabled={saving} data-testid="admin-save-drill-btn">
              {saving ? 'Saving...' : drill ? 'Save Changes' : 'Create Drill'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── CSV Import Modal ─── */
function DrillImportModal({ open, onClose, onImported }) {
  const [csvText, setCsvText] = useState('');
  const [importing, setImporting] = useState(false);

  const handleImport = async () => {
    const parseCSVLine = (line) => {
      const fields = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
          else { inQuotes = !inQuotes; }
        } else if (ch === ',' && !inQuotes) {
          fields.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
      fields.push(current.trim());
      return fields;
    };

    const lines = csvText.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) { toast.error('CSV must have a header row and at least one data row'); return; }
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
    const drills = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = parseCSVLine(lines[i]);
      const row = {};
      headers.forEach((h, j) => { row[h] = vals[j] || ''; });
      if (row.name) drills.push(row);
    }
    if (drills.length === 0) { toast.error('No valid drills found in CSV'); return; }
    setImporting(true);
    try {
      const res = await api.post('/drills/import-csv', { drills });
      toast.success(res.data.message);
      setCsvText('');
      onImported();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-bold uppercase">Import Drills from CSV</DialogTitle></DialogHeader>
        <div className="py-2 space-y-3">
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
            <p className="font-bold mb-1">CSV Format:</p>
            <code className="block bg-white p-2 rounded text-[11px] font-mono overflow-x-auto">name,description,duration_minutes,tags,category,difficulty,equipment,video_url</code>
            <p className="mt-1">Tags & equipment: semicolon-separated. Category: Warm-Up, Skill Work, Scrimmage, Cool-Down. Video: YouTube URL</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Upload CSV File</label>
            <input type="file" accept=".csv,.txt" onChange={handleFileUpload} className="text-sm" data-testid="admin-drill-csv-upload" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Or paste CSV text</label>
            <textarea className="w-full border border-slate-200 rounded-lg p-2 text-xs font-mono h-[200px] resize-y focus:ring-1 focus:ring-slate-400 focus:outline-none"
              value={csvText} onChange={e => setCsvText(e.target.value)} placeholder="name,description,duration_minutes,tags,category..." />
          </div>
          <div className="flex gap-3 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1 bg-slate-900" onClick={handleImport} disabled={importing || !csvText.trim()} data-testid="admin-confirm-import-btn">
              {importing ? 'Importing...' : 'Import Drills'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
